import { X } from "lucide-react";
import { type ChangeEvent, useId, useMemo } from "react";
import { TextArea } from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { NativeSelect } from "@/components/base/select/select-native";
import { useAppTranslation } from "@/hooks/use-app-translation";
import {
	API_NUMBER_DATA_TYPES,
	type ApiNumberDataType,
} from "@/lib/dynamicForms/constants/api-number-data-type.constant";
import { cx } from "@/utils/cx";
import { FieldOptionListEditor } from "./field-option-list-editor";
import { useAdminFormEditorPaths } from "../hooks/use-admin-form-editor-paths";
import { createDefaultField } from "../lib/create-default-field";
import { PALETTE_ITEMS } from "../lib/palette-metadata";
import { useFormEditorStore } from "../state/form-editor-store";
import type { BuilderFieldData, BuilderNode, PaletteFieldType } from "../types";

function readTextFieldChangeValue(
	value: string | ChangeEvent<HTMLInputElement>,
): string {
	if (typeof value === "string") return value;
	return value.target?.value ?? "";
}

const TEXTAREA_FIELD_CLASS =
	"min-h-form-properties-textarea w-full resize-y rounded-lg bg-primary px-3 py-2 text-md text-primary shadow-xs ring-1 ring-primary ring-inset transition-shadow placeholder:text-placeholder focus:outline-none focus:ring-2 focus:ring-brand disabled:cursor-not-allowed disabled:bg-disabled_subtle";

interface FieldPropertiesPanelProps {
	nodeId: string;
	node: BuilderNode;
	onClose: () => void;
}

/**
 * Right rail: field or section properties when an item is selected; otherwise the palette is shown.
 */
export function FieldPropertiesPanel({
	nodeId,
	node,
	onClose,
}: FieldPropertiesPanelProps) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const updateField = useFormEditorStore((s) => s.updateField);
	const updateSection = useFormEditorStore((s) => s.updateSection);
	const deleteNode = useFormEditorStore((s) => s.deleteNode);
	const sectionDescriptionFieldId = useId();

	if (node.kind === "section") {
		const { section, childFieldIds } = node;
		return (
			<>
				<header className="flex items-start justify-between gap-2 border-b border-border-secondary px-6 py-4">
					<div>
						<h2
							id="form-builder-properties-heading"
							className="text-lg font-semibold text-text-primary"
						>
							{t(paths.formEditor.properties.sectionTitle)}
						</h2>
						<p className="text-sm text-text-tertiary">
							{t(paths.formEditor.properties.subtitle)}
						</p>
					</div>
					<Button
						size="sm"
						color="tertiary"
						type="button"
						iconLeading={X}
						onClick={onClose}
						aria-label={t(paths.formEditor.properties.close)}
					/>
				</header>
				<form
					className="flex flex-1 flex-col gap-5 overflow-y-auto p-6"
					onSubmit={(e) => {
						e.preventDefault();
					}}
					noValidate
				>
					<Input
						label={t(paths.formEditor.properties.sectionLabel)}
						value={section.label}
						placeholder={t(paths.formEditor.properties.sectionLabelPlaceholder)}
						onChange={(v) => {
							updateSection(nodeId, { label: readTextFieldChangeValue(v) });
						}}
					/>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor={sectionDescriptionFieldId}>
							{t(paths.formEditor.properties.description)}
						</Label>
						<TextArea
							id={sectionDescriptionFieldId}
							value={section.helpText ?? ""}
							onChange={(e) => {
								updateSection(nodeId, {
									helpText: (e as ChangeEvent<HTMLTextAreaElement>).target
										?.value,
								});
							}}
							placeholder={t(
								paths.formEditor.properties.descriptionPlaceholder,
							)}
							className={TEXTAREA_FIELD_CLASS}
							rows={3}
						/>
					</div>
					<Checkbox
						isSelected={section.collapsible}
						onChange={(checked) => {
							updateSection(nodeId, { collapsible: checked });
						}}
						label={t(paths.formEditor.properties.collapsible)}
					/>
					{section.collapsible ? (
						<div className={cx("ml-6")}>
							<Checkbox
								isSelected={section.defaultExpanded}
								onChange={(checked) => {
									updateSection(nodeId, { defaultExpanded: checked });
								}}
								label={t(paths.formEditor.properties.defaultExpanded)}
							/>
						</div>
					) : null}
					<div className="rounded-md border border-brand-200 bg-brand-50 p-3 text-xs text-brand-700">
						{t(paths.formEditor.properties.sectionTip)}
					</div>
					<Button
						color="secondary-destructive"
						size="sm"
						className="w-full"
						type="button"
						onClick={() => {
							deleteNode(nodeId);
						}}
					>
						{t(paths.formEditor.properties.deleteSection, {
							count: childFieldIds.length,
						})}
					</Button>
				</form>
			</>
		);
	}

	const field = node.field;
	return (
		<FieldPropertiesForm
			field={field}
			onClose={onClose}
			onUpdate={(partial) => {
				updateField(nodeId, partial);
			}}
			onDelete={() => {
				deleteNode(nodeId);
			}}
		/>
	);
}

function FieldPropertiesForm({
	field,
	onClose,
	onUpdate,
	onDelete,
}: {
	field: BuilderFieldData;
	onClose: () => void;
	onUpdate: (partial: Partial<BuilderFieldData>) => void;
	onDelete: () => void;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const formEditorEntity = useFormEditorStore((s) => s.formEditorEntity);
	/** Checkbox is boolean-only in the builder — no option list (use toggleButton for multi-choice). */
	const hasOptions =
		field.fieldType === "select" ||
		field.fieldType === "radio" ||
		field.fieldType === "multiSelect";

	const isFile = field.fieldType === "file";

	const paletteOptions = useMemo(
		() =>
			PALETTE_ITEMS.map((item) => {
				const typeKey =
					item.labelKey as keyof typeof paths.formEditor.palette.types;
				return {
					value: item.type,
					label: String(t(paths.formEditor.palette.types[typeKey])),
				};
			}),
		[t, paths],
	);

	return (
		<>
			<header className="flex items-start justify-between gap-2 border-b border-border-secondary px-6 py-4">
				<div>
					<h2
						id="form-builder-properties-heading"
						className="text-lg font-semibold text-text-primary"
					>
						{t(paths.formEditor.properties.fieldTitle)}
					</h2>
					<p className="text-sm text-text-tertiary">
						{t(paths.formEditor.properties.subtitle)}
					</p>
				</div>
				<Button
					size="sm"
					color="tertiary"
					type="button"
					iconLeading={X}
					onClick={onClose}
					aria-label={t(paths.formEditor.properties.close)}
				/>
			</header>
			<form
				className="flex flex-1 flex-col gap-5 overflow-y-auto p-6"
				onSubmit={(e) => {
					e.preventDefault();
				}}
				noValidate
			>
				<NativeSelect
					label={t(paths.formEditor.properties.fieldType)}
					options={paletteOptions}
					value={field.fieldType}
					onChange={(e) => {
						const nextType = e.target.value as PaletteFieldType;
						const defaults = createDefaultField(
							nextType,
							field.id,
							field.label,
							formEditorEntity,
						);
						onUpdate({
							...defaults,
							label: field.label,
							name: field.name,
						});
					}}
				/>
				<Input
					label={t(paths.formEditor.properties.fieldLabel)}
					value={field.label}
					placeholder={t(paths.formEditor.properties.fieldLabelPlaceholder)}
					onChange={(v) => {
						onUpdate({ label: readTextFieldChangeValue(v) });
					}}
				/>
				{field.fieldType === "number" ? (
					<NativeSelect
						label={t(paths.formEditor.properties.numberApiDataType)}
						hint={t(paths.formEditor.properties.numberApiDataTypeHint)}
						value={field.numberApiDataType ?? API_NUMBER_DATA_TYPES.Decimal}
						onChange={(e) => {
							onUpdate({
								numberApiDataType: e.target.value as ApiNumberDataType,
							});
						}}
						options={[
							{
								value: API_NUMBER_DATA_TYPES.Integer,
								label: t(paths.formEditor.properties.numberApiDataTypeInteger),
							},
							{
								value: API_NUMBER_DATA_TYPES.Decimal,
								label: t(paths.formEditor.properties.numberApiDataTypeDecimal),
							},
						]}
					/>
				) : null}
				{field.fieldType === "textarea" ? (
					<Input
						label={t(paths.formEditor.properties.textAreaRows)}
						type="number"
						min={1}
						max={50}
						hint={t(paths.formEditor.properties.textAreaRowsHint)}
						value={
							field.textAreaRows === undefined ? "" : String(field.textAreaRows)
						}
						placeholder="3"
						onChange={(v) => {
							const raw = readTextFieldChangeValue(v).trim();
							if (raw === "") {
								onUpdate({ textAreaRows: undefined });
								return;
							}
							const n = Number.parseInt(raw, 10);
							if (!Number.isFinite(n) || n < 1) return;
							onUpdate({ textAreaRows: Math.min(n, 50) });
						}}
					/>
				) : null}
				<Input
					label={
						isFile
							? t(paths.formEditor.properties.dropZoneHint)
							: t(paths.formEditor.properties.placeholder)
					}
					value={field.placeholder ?? ""}
					placeholder={
						isFile
							? t(paths.formEditor.properties.dropZoneHintPlaceholder)
							: t(paths.formEditor.properties.placeholderHint)
					}
					onChange={(v) => {
						onUpdate({ placeholder: readTextFieldChangeValue(v) });
					}}
				/>
				<Input
					label={t(paths.formEditor.properties.helpText)}
					value={field.helpText ?? ""}
					onChange={(v) => {
						onUpdate({ helpText: readTextFieldChangeValue(v) });
					}}
				/>
				<Checkbox
					isSelected={field.required}
					onChange={(checked) => {
						onUpdate({ required: checked });
					}}
					label={t(paths.formEditor.properties.required)}
				/>

				{isFile ? (
					<>
						<NativeSelect
							label={t(paths.formEditor.properties.filePresentation)}
							value={field.filePresentation ?? "dropZone"}
							onChange={(e) => {
								onUpdate({
									filePresentation: e.target.value as "dropZone" | "button",
								});
							}}
							options={[
								{
									value: "dropZone",
									label: t(
										paths.formEditor.properties.filePresentationDropZone,
									),
								},
								{
									value: "button",
									label: t(paths.formEditor.properties.filePresentationButton),
								},
							]}
						/>
						<Input
							label={t(paths.formEditor.properties.fileAccept)}
							value={field.fileAccept ?? ""}
							placeholder={t(paths.formEditor.properties.fileAcceptPlaceholder)}
							hint={t(paths.formEditor.properties.fileAcceptHint)}
							onChange={(v) => {
								const raw = readTextFieldChangeValue(v).trim();
								onUpdate({
									fileAccept: raw === "" ? undefined : raw,
								});
							}}
						/>
						<Checkbox
							isSelected={field.fileMultiple !== false}
							onChange={(checked) => {
								onUpdate({
									fileMultiple: checked,
									...(!checked ? { fileMaxFiles: undefined } : {}),
								});
							}}
							label={t(paths.formEditor.properties.allowMultipleFiles)}
						/>
						{field.fileMultiple !== false ? (
							<Input
								label={t(paths.formEditor.properties.maxFiles)}
								type="number"
								min={1}
								value={
									field.fileMaxFiles === undefined
										? ""
										: String(field.fileMaxFiles)
								}
								placeholder={t(paths.formEditor.properties.maxFilesPlaceholder)}
								hint={t(paths.formEditor.properties.maxFilesHint)}
								onChange={(v) => {
									const raw = readTextFieldChangeValue(v).trim();
									if (raw === "") {
										onUpdate({ fileMaxFiles: undefined });
										return;
									}
									const n = Number.parseInt(raw, 10);
									if (!Number.isFinite(n) || n < 1) return;
									onUpdate({ fileMaxFiles: Math.min(n, 999) });
								}}
							/>
						) : null}
					</>
				) : null}

				{hasOptions ? (
					<FieldOptionListEditor
						fieldId={field.id}
						options={field.options}
						reorderable={
							field.fieldType === "select" ||
							field.fieldType === "multiSelect"
						}
						onChange={(options) => onUpdate({ options })}
					/>
				) : null}

				<Button
					color="secondary-destructive"
					size="sm"
					className="w-full"
					type="button"
					onClick={onDelete}
				>
					{t(paths.formEditor.properties.deleteField)}
				</Button>
			</form>
		</>
	);
}
